import type { QueryView } from '@krp/client-runtime'
import type { ActiveDay, ChoiceChip, DayRange, LoadPart, StatItem, TreeNavNode, TrendPoint, ReceiptsTableRow } from '@krp/ui-kit'

/** Вид кассового документа 1С: чек ККМ или чек ККМ на возврат. */
export type CashReceiptKind = 'sale' | 'refund'

/** Касса и её место в структуре: организация и магазин (структурная единица); `null` — не определилось. */
export interface CashRegister {
  key: string
  name: string
  code: string | null
  organizationKey: string | null
  unitKey: string | null
}

export interface CashOrganization {
  key: string
  name: string
}

export type CashUnitKind = 'department' | 'warehouse' | 'other'

/** Структурная единица базы (магазин, склад, подразделение), в которой стоят кассы. */
export interface CashUnit {
  key: string
  name: string
  kind: CashUnitKind
  organizationKey: string | null
  parentKey: string | null
}

export interface CashRegisters {
  registers: readonly CashRegister[]
  organizations: readonly CashOrganization[]
  units: readonly CashUnit[]
  observedAt: string
}

/** Чек 1С в списке; поле, которого база не публикует, — `null` («не знаем», а не ноль). */
export interface CashReceipt {
  key: string
  /** Чек был продажей (проведён, не отложен); иначе он в списке есть, а в итоги не входит. */
  countsInSales: boolean
  kind: CashReceiptKind
  number: string | null
  /** Дата 1С — местное время без пояса: `2026-10-07T10:15:00`. */
  date: string | null
  posted: boolean
  registerKey: string | null
  registerName: string | null
  receiptNumber: string | null
  status: string | null
  amount: number | null
  cardAmount: number | null
  /** Кассир чека (пользователь базы); `null` — база его не отдала. */
  cashierKey: string | null
  cashierName: string | null
  /** Номер кассовой смены, в которой пробит чек. */
  shiftNumber: string | null
}

export interface CashReceipts {
  items: readonly CashReceipt[]
  from: string
  to: string
  truncated: boolean
  observedAt: string
}

/** Срез статистики: чеки одного вида за день у кассы и кассира (рубли). Из таких строк собираются итоги, дни, кассиры и график. */
export interface CashSummaryRow {
  day: string
  registerKey: string | null
  cashierKey: string | null
  kind: CashReceiptKind
  count: number
  amount: number
  cardAmount: number
}

export interface CashSummary {
  from: string
  to: string
  rows: readonly CashSummaryRow[]
  cashiers: readonly { key: string; name: string }[]
  truncated: boolean
  observedAt: string
}

export interface CashReceiptLine {
  lineNumber: number
  productKey: string | null
  productName: string | null
  quantity: number
  price: number | null
  amount: number | null
  discount: number | null
}

export interface CashReceiptDetails {
  receipt: CashReceipt
  lines: readonly CashReceiptLine[]
  payments: readonly number[]
  observedAt: string
}

/** Вид чека в отборе экрана: пусто — все. */
export type CashKindFilter = '' | CashReceiptKind

/**
 * Отбор экрана одной базы: узел дерева (`''` — вся база), период (`YYYY-MM-DD`, `null` — сегодня), вид чека, поиск,
 * свёрнутые узлы дерева и раскрытый чек (`kind:key`).
 */
export interface CashReceiptsViewState {
  nodeId: string
  from: string | null
  to: string | null
  kind: CashKindFilter
  /** Кассир отбора — ключ пользователя 1С; пусто — все. */
  cashier: string
  search: string
  collapsed: string[]
  /** Метка последнего «Обновить» (мс); `0` — не обновляли. Входит в запросы и обходит память службы и кэш браузера. */
  refreshStamp: number
}

export type CashReceiptsIntentState = Record<string, CashReceiptsViewState>

export const EMPTY_RECEIPTS_VIEW: CashReceiptsViewState = {
  nodeId: '',
  from: null,
  to: null,
  kind: '',
  cashier: '',
  search: '',
  collapsed: [],
  refreshStamp: 0,
}

/** Что просить у службы: период и кассы выбранного узла (пусто — все кассы базы). Ключи отсортированы — ключ кэша стабилен. */
export interface CashReceiptsArgs {
  resourceId: string
  from: string
  to: string
  registerKeys: readonly string[]
  /** Метка «Обновить»: служба читает 1С заново, а не отдаёт из памяти. */
  fresh?: number
}

/** Статистика за период: служба читает 1С кусками и отдаёт только срезы, без чеков. */
export interface CashSummaryArgs {
  resourceId: string
  from: string
  to: string
  fresh?: number
}

/** С какого и по какой день в базе 1С есть чеки; `null` — чеков нет. */
export interface CashBounds {
  first: string | null
  last: string | null
  observedAt: string
}

export interface CashBoundsArgs {
  resourceId: string
  fresh?: number
}

export interface CashReceiptArgs {
  resourceId: string
  kind: CashReceiptKind
  key: string
}

/** Строка общей таблицы чеков (`ReceiptsTable`) плюс то, что есть только у 1С: смена и состояние документа. */
export interface CashReceiptRowVm extends ReceiptsTableRow {
  kind: CashReceiptKind
  /** Ключ документа 1С — адрес окна чека. */
  receiptKey: string
  /** Номер смены; пусто — база его не отдала. */
  shift: string
  /** «Проведён», «Отложен» и состояние документа 1С — словами. */
  status: string
}


/** Дерево «Где смотрим»: организации → магазины и склады → кассы базы. */
export interface CashReceiptsNavVm {
  query: QueryView
  nodes: readonly TreeNavNode[]
  selectedId: string
  /** Быстрый выбор: вся база и магазины с кассами, без иерархии организаций. */
  stores: readonly Pick<TreeNavNode, 'id' | 'label' | 'caption' | 'badge'>[]
  /** Выбранное словами: «Вся база», «ООО Ромашка · Магазин у дома · Касса зала». */
  summaryText: string
  /** Кассы не прочитаны: чеки при этом показываются по всей базе, но выбирать место нельзя. */
  note: string | null
}

export interface CashReceiptsScreenVm {
  today: string
  period: DayRange
  nav: CashReceiptsNavVm
  /** Крупные карточки по выбранному месту и периоду: приход, возвраты, итог, число чеков. */
  stats: readonly StatItem[]
  statsBusy?: boolean
  /** Дни, в которые в выбранном месте за период были чеки. */
  days: readonly ActiveDay[]
  kinds: readonly ChoiceChip[]
  kind: CashKindFilter
  /**
   * `day` — период из одного дня: показан список его чеков. `range` — период из нескольких дней: чеки не выгружаются,
   * только статистика, график и дни, из которых можно открыть день.
   */
  mode: 'day' | 'range'
  /** Динамика периода (в режиме `range`): по дням или месяцам. */
  trend: readonly TrendPoint[]
  /** Динамика по месяцам (или дням) для периода длиннее месяца: «Весь год», «За всё время». */
  periodTrend: readonly TrendPoint[]
  /** Самый ранний день с чеками в базе; `null` — неизвестен или чеков нет. */
  firstDay: string | null
  /** Пояснение, если выбранный период начинается раньше первого чека базы. */
  boundsNote: string | null
  /** Идёт чтение, запущенное «Обновить» или сменой периода. */
  refreshing: boolean
  /** «Обновить» перечитывает сегодняшний день: загрузка — на его столбце графика, прошлые дни остаются на экране. */
  todayBusy: boolean
  /** Состояние каждого месяца периода при чтении по месяцам: ждёт очереди, читается, прочитан, не прочитан. */
  loadParts: readonly LoadPart[]
  trendQuery?: QueryView
  /** Период читается по месяцам и ещё не дочитан: сколько прочитано словами; `null` — всё прочитано. */
  progressNote: string | null
  /** С какого дня начинается «За всё время». */
  allTimeFrom: string
  /** Кассиры места и периода (пользователи базы из чеков); первый — «Все кассиры». */
  cashiers: readonly ChoiceChip[]
  cashier: string
  search: string
  /** Период не по правилам — запроса нет, человеку сказано почему. */
  periodError: string | null
  query: QueryView
  readAt: string | null
  truncatedNote: string | null
  /** Чеки за период в базе есть, а в выбранном месте, по виду и поиску — ни одного. */
  filteredOut: boolean
  rows: readonly CashReceiptRowVm[]
}

export interface CashReceiptsScreenActions {
  nodeChosen: (nodeId: string) => void
  nodeToggled: (nodeId: string) => void
  periodChanged: (range: DayRange) => void
  kindChanged: (kind: CashKindFilter) => void
  cashierChanged: (cashier: string) => void
  searchChanged: (value: string) => void
  /** Открыть окно чека поверх экрана: строка таблицы — вход в него. */
  openReceipt: (row: CashReceiptRowVm) => void
  retry: () => void
  /** «Обновить»: перечитать из 1С всё, что видно на экране (кэш службы и браузера обходится). */
  refresh: () => void
  /** Перечитать только месяцы, которые не прочитались. */
  retryMonths: () => void
}
